import { LinkBox } from '@Pages/pageRuntime/components/ui/LinkOverlay';
import { cn } from '@Pages/pageRuntime/lib/utils';
import * as React from 'react';

const Table = ({
  className,
  containerClassName,
  containerLabel = 'Scrollable table',
  ref,
  ...props
}: React.ComponentProps<'table'> & { containerClassName?: string; containerLabel?: string }) => (
  <div
    data-slot="table-container"
    role="region"
    aria-label={containerLabel}
    tabIndex={0}
    className={cn(
      'relative w-full overflow-x-auto rounded-[var(--radius)] outline-none focus-visible:ring-1 focus-visible:ring-ring',
      containerClassName
    )}
  >
    <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
  </div>
);
Table.displayName = 'Table';

const TableHeader = ({ className, ref, ...props }: React.ComponentProps<'thead'>) => (
  <thead ref={ref} className={cn('border-border [&_tr]:border-b', className)} {...props} />
);
TableHeader.displayName = 'TableHeader';

const TableBody = ({ className, ref, ...props }: React.ComponentProps<'tbody'>) => (
  <tbody
    ref={ref}
    className={cn('border-border [&_tr:last-child]:border-0', className)}
    {...props}
  />
);
TableBody.displayName = 'TableBody';

const TableFooter = ({ className, ref, ...props }: React.ComponentProps<'tfoot'>) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t border-border bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
);
TableFooter.displayName = 'TableFooter';

const TableRow = ({
  className,
  interactive,
  ref,
  ...props
}: React.ComponentProps<'tr'> & { interactive?: boolean }) => {
  const tr = (
    <tr
      ref={ref}
      className={cn(
        'border-b border-border transition-colors data-[state=selected]:bg-muted',
        {
          'cursor-pointer hover:bg-muted/50': interactive || !!props.onClick
        },
        className
      )}
      {...props}
    />
  );

  if (interactive) {
    return <LinkBox asChild>{tr}</LinkBox>;
  }

  return tr;
};
TableRow.displayName = 'TableRow';

const TableHead = ({ className, ref, ...props }: React.ComponentProps<'th'>) => (
  <th
    ref={ref}
    className={cn(
      'h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
);
TableHead.displayName = 'TableHead';

const TableCell = ({ className, ref, ...props }: React.ComponentProps<'td'>) => (
  <td
    ref={ref}
    className={cn(
      'p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
);
TableCell.displayName = 'TableCell';

const TableCaption = ({ className, ref, ...props }: React.ComponentProps<'caption'>) => (
  <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
);
TableCaption.displayName = 'TableCaption';

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
